import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  dollarsStringToCents,
  formatCentsAsCurrency,
} from '../Utils/money';
import { formatEasternDateTime } from '../Utils/dateTime';
import {
  getErrorMessage,
  getOrderPaymentsLedger,
  postPaymentLinkSend,
  postPaymentRefund,
} from '../Config/adminOrderFlowService';
import OfflinePaymentForm from './OfflinePaymentForm';

const REFUND_REASONS = [
  { value: 'requested_by_customer', label: 'Requested by customer' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
];

function moneyCents(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function paymentStatusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCEEDED') return 'bg-green-100 text-green-800';
  if (s === 'CANCELED' || s === 'CANCELLED') return 'bg-red-100 text-red-800';
  if (s === 'PROCESSING' || s === 'REQUIRES_CAPTURE') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
}

function refundStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'succeeded') return 'bg-green-100 text-green-800';
  if (s === 'failed') return 'bg-red-100 text-red-800';
  if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
}

function orderPaymentStatusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'bg-green-100 text-green-800';
  if (s === 'PAYMENT_FAILED') return 'bg-red-100 text-red-800';
  if (s === 'PROCESSING' || s === 'AUTHORIZED') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
}

function PaymentRefundControls({ payment, currency, disabled, onRefunded }) {
  const paymentId = payment.payment_id;
  const ledger = payment.ledger ?? {};
  const maxCents = moneyCents(ledger.refundable_remaining_cents);
  const [open, setOpen] = useState(false);
  const [partial, setPartial] = useState(false);
  const [amountDollars, setAmountDollars] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [busy, setBusy] = useState(false);

  if (!ledger.can_refund_via_admin || !paymentId) return null;

  const submit = async () => {
    if (disabled || busy) return;
    const payload = { reason };
    if (partial) {
      const cents = dollarsStringToCents(amountDollars);
      if (Number.isNaN(cents) || cents <= 0) {
        toast.error('Enter a valid partial refund amount');
        return;
      }
      if (cents > maxCents) {
        toast.error('Amount exceeds refundable balance for this payment');
        return;
      }
      payload.amount_cents = cents;
    }
    setBusy(true);
    try {
      const { ok, body } = await postPaymentRefund(paymentId, payload);
      if (ok && body.success !== false) {
        const data = body.data ?? body;
        toast.success(
          `Refund ${formatCentsAsCurrency(
            moneyCents(data.amount_cents ?? payload.amount_cents ?? maxCents),
            currency
          )} submitted`
        );
        setOpen(false);
        setPartial(false);
        setAmountDollars('');
        if (onRefunded) await onRefunded();
      } else {
        toast.error(getErrorMessage(body, 'Refund failed'));
      }
    } catch {
      toast.error('Refund failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-50 disabled:opacity-50"
      >
        Refund
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-rose-200 bg-rose-50/60 p-2 text-left">
      <p className="text-xs font-medium text-rose-950">
        Refund up to {formatCentsAsCurrency(maxCents, currency)}
      </p>
      <label className="flex items-center gap-2 text-xs text-gray-700">
        <input
          type="checkbox"
          checked={partial}
          onChange={(e) => setPartial(e.target.checked)}
          disabled={disabled || busy}
        />
        Partial refund
      </label>
      {partial ? (
        <input
          type="number"
          step="0.01"
          min="0"
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          placeholder="Amount (dollars)"
          value={amountDollars}
          onChange={(e) => setAmountDollars(e.target.value)}
          disabled={disabled || busy}
        />
      ) : null}
      <select
        className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={disabled || busy}
      >
        {REFUND_REASONS.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled || busy}
          onClick={submit}
          className="rounded-md bg-rose-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-800 disabled:opacity-50"
        >
          {busy ? 'Refunding…' : 'Confirm'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setPartial(false);
            setAmountDollars('');
          }}
          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Payment information block powered by GET …/payments-ledger.
 */
function OrderPaymentsLedgerPanel({
  orderId,
  disabled = false,
  openOfflinePaymentRequest = 0,
  onOfflinePaymentRecorded,
}) {
  const [ledger, setLedger] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [showOfflinePaymentForm, setShowOfflinePaymentForm] = useState(false);
  const fetchGenRef = useRef(0);

  const orderIdKey = orderId != null && String(orderId).trim() !== '' ? String(orderId) : '';

  const loadLedger = useCallback(async ({ silent = false } = {}) => {
    if (!orderIdKey) {
      setInitialLoading(false);
      setRefreshing(false);
      return;
    }

    const gen = ++fetchGenRef.current;
    if (silent) {
      setRefreshing(true);
    } else {
      setRefreshing(false);
    }
    setError(null);

    try {
      const { ok, body } = await getOrderPaymentsLedger(orderIdKey);
      if (gen !== fetchGenRef.current) return;

      if (ok && body.success !== false) {
        setLedger(body.data ?? body);
      } else {
        setLedger(null);
        setError(getErrorMessage(body, 'Failed to load payments'));
      }
    } catch {
      if (gen !== fetchGenRef.current) return;
      setLedger(null);
      setError('Failed to load payments');
    } finally {
      if (gen === fetchGenRef.current) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, [orderIdKey]);

  useEffect(() => {
    fetchGenRef.current += 1;
    setLedger(null);
    setError(null);
    setInitialLoading(Boolean(orderIdKey));
    setRefreshing(false);

    if (!orderIdKey) {
      setInitialLoading(false);
      return undefined;
    }

    loadLedger();
    return () => {
      fetchGenRef.current += 1;
    };
  }, [orderIdKey, loadLedger]);

  useEffect(() => {
    if (openOfflinePaymentRequest > 0 && !disabled) {
      setShowOfflinePaymentForm(true);
    }
  }, [openOfflinePaymentRequest, disabled]);

  const refreshLedger = useCallback(() => loadLedger({ silent: true }), [loadLedger]);

  const currency = String(ledger?.meta?.currency ?? 'CAD').toUpperCase();
  const summary = ledger?.summary;
  const payments = Array.isArray(ledger?.payments) ? ledger.payments : [];
  const walletRows = Array.isArray(ledger?.wallet_transactions) ? ledger.wallet_transactions : [];
  const paymentLink = ledger?.payment_link;
  const amountRemaining = summary != null ? moneyCents(summary.amount_remaining_cents) : null;

  const sendPaymentLinkSms = async () => {
    if (!orderIdKey || disabled) return;
    const numericId = Number(orderIdKey);
    if (!Number.isFinite(numericId)) {
      toast.error('Invalid order id');
      return;
    }
    setSmsBusy(true);
    try {
      const { ok, body } = await postPaymentLinkSend(numericId);
      if (ok && body.success !== false) {
        toast.success(body.message || 'Payment link SMS queued');
        await refreshLedger();
      } else {
        toast.error(getErrorMessage(body, 'Could not queue payment link SMS'));
      }
    } catch {
      toast.error('Could not queue payment link SMS');
    } finally {
      setSmsBusy(false);
    }
  };

  if (!orderIdKey) {
    return <p className="text-sm text-gray-500">Order id missing.</p>;
  }

  if (initialLoading && !ledger) {
    return <p className="text-sm text-gray-500">Loading payment ledger…</p>;
  }

  if (error && !ledger) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-700">{error}</p>
        <button
          type="button"
          onClick={() => loadLedger()}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!ledger) return null;

  const showPaymentSection =
    summary != null ||
    ledger.payment_status != null ||
    payments.length > 0 ||
    walletRows.length > 0 ||
    paymentLink != null;

  if (!showPaymentSection) {
    return <p className="text-sm text-gray-500">No payment activity for this order.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!disabled && amountRemaining != null && amountRemaining > 0 ? (
          <button
            type="button"
            onClick={() => setShowOfflinePaymentForm(true)}
            className="rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800"
          >
            Record offline payment
          </button>
        ) : null}
        <button
          type="button"
          disabled={refreshing}
          onClick={refreshLedger}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {summary ? (
        <div className="grid gap-3 rounded-lg border border-sky-200 bg-sky-50/80 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/70">Invoice total</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums">
              {formatCentsAsCurrency(moneyCents(summary.amount_total_cents), currency)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/70">Received (net)</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-800">
              {formatCentsAsCurrency(moneyCents(summary.amount_received_cents), currency)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/70">Remaining</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums text-amber-900">
              {formatCentsAsCurrency(moneyCents(summary.amount_remaining_cents), currency)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/70">Refunded (succeeded)</p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums text-rose-800">
              {formatCentsAsCurrency(moneyCents(summary.refunded_total_succeeded_cents), currency)}
            </p>
            <p className="mt-1 text-[11px] text-gray-600">
              {summary.payments_count ?? payments.length} payment(s) · {summary.refunds_count ?? 0} refund(s)
            </p>
          </div>
        </div>
      ) : null}

      {(ledger.payment_status != null || ledger.has_active_payment != null) && (
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {ledger.payment_status != null ? (
              <div className="text-left">
                <span className="font-semibold">Payment status:</span>
                <span className={`ml-2 rounded px-2 py-1 text-sm ${orderPaymentStatusClass(ledger.payment_status)}`}>
                  {ledger.payment_status}
                </span>
              </div>
            ) : null}
            {ledger.has_active_payment != null ? (
              <div className="text-left">
                <span className="font-semibold">Has active payment:</span>{' '}
                {ledger.has_active_payment ? 'Yes' : 'No'}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {!disabled && amountRemaining != null && amountRemaining > 0 ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 text-left">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-indigo-950">Customer payment link</h3>
              <p className="mt-1 text-xs text-indigo-900/80">
                Customer still owes {formatCentsAsCurrency(amountRemaining, currency)}. Sends an SMS with
                the pay link (queued immediately; link is created in the worker).
              </p>
              {paymentLink ? (
                <p className="mt-1 text-xs text-gray-600">
                  Link on file: {paymentLink.is_active ? 'active' : 'inactive'}
                  {paymentLink.revoked_at ? ` · revoked ${formatEasternDateTime(paymentLink.revoked_at)}` : ''}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              disabled={smsBusy}
              onClick={sendPaymentLinkSms}
              className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {smsBusy ? 'Sending…' : 'Send payment link by SMS'}
            </button>
          </div>
        </div>
      ) : null}

      {showOfflinePaymentForm && !disabled ? (
        <div id="offline-payment-form">
          <OfflinePaymentForm
            orderId={orderIdKey}
            currency={currency}
            onCancel={() => setShowOfflinePaymentForm(false)}
            onLedgerRefresh={(nextLedger) => {
              if (nextLedger) setLedger(nextLedger);
            }}
            onRecorded={async (nextLedger, result) => {
              if (nextLedger) setLedger(nextLedger);
              const nextRemaining = Number(nextLedger?.summary?.amount_remaining_cents);
              if (Number.isFinite(nextRemaining) && nextRemaining === 0) {
                setShowOfflinePaymentForm(false);
              }
              if (onOfflinePaymentRecorded) {
                await onOfflinePaymentRecorded(nextLedger, result);
              }
            }}
          />
        </div>
      ) : null}

      {payments.length > 0 ? (
        <div className="rounded-lg bg-blue-50 p-4">
          <h3 className="mb-3 text-left text-lg font-semibold text-blue-800">Payments</h3>
          <div className="space-y-4">
            {payments.map((payment) => {
              const hints = payment.stripe_hints ?? {};
              const card = hints.payment_method_details;
              const ledgerRow = payment.ledger ?? {};
              const refunds = Array.isArray(payment.refunds) ? payment.refunds : [];
              const pgType = String(payment.pg_type || 'STRIPE').toUpperCase();
              const collector =
                payment.collector ??
                payment.collected_by ??
                payment.collected_by_user ??
                null;
              const recorder =
                payment.recorder ??
                payment.recorded_by ??
                payment.recorded_by_user ??
                null;
              const collectorName =
                collector?.name ??
                payment.collector_name ??
                payment.collected_by_name ??
                payment.collected_by_user_id;
              const collectorRole =
                collector?.role ?? payment.collector_role ?? payment.collected_by_role;
              const recorderName =
                recorder?.name ??
                payment.recorded_by_name ??
                payment.recorded_by_user_id;
              const paymentWallet = payment.wallet_transaction ?? payment.wallet ?? null;
              return (
                <div
                  key={payment.payment_id}
                  className="overflow-hidden rounded-lg border border-blue-100 bg-white shadow-sm"
                >
                  <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
                    <div className="text-left md:col-span-2">
                      <span className="font-semibold">Payment ID:</span>{' '}
                      <span className="font-mono text-sm">{payment.payment_id}</span>
                    </div>
                    <div className="text-left">
                      <span className="font-semibold">Amount:</span>{' '}
                      {formatCentsAsCurrency(moneyCents(payment.amount_cents), currency)}
                    </div>
                    <div className="text-left">
                      <span className="font-semibold">Status:</span>
                      <span className={`ml-2 rounded px-2 py-1 text-sm ${paymentStatusClass(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                    <div className="text-left text-sm text-gray-700">
                      <span className="font-semibold">Payment type:</span>{' '}
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold">{pgType}</span>
                      {payment.mode ? (
                        <span className="ml-2 rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">
                          {payment.mode}
                        </span>
                      ) : null}
                    </div>
                    {pgType === 'OFFLINE' && collectorName ? (
                      <div className="text-left text-sm text-gray-700">
                        <span className="font-semibold">Collected by:</span> {collectorName}
                        {collectorRole ? ` (${collectorRole})` : ''}
                      </div>
                    ) : null}
                    {pgType === 'OFFLINE' && recorderName ? (
                      <div className="text-left text-sm text-gray-700">
                        <span className="font-semibold">Recorded by:</span> {recorderName}
                      </div>
                    ) : null}
                    {pgType === 'OFFLINE' && payment.external_reference ? (
                      <div className="text-left text-sm text-gray-700">
                        <span className="font-semibold">Reference:</span> {payment.external_reference}
                      </div>
                    ) : null}
                    {pgType === 'OFFLINE' && payment.proof_url ? (
                      <div className="text-left text-sm">
                        <a
                          href={payment.proof_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-700 underline hover:text-indigo-900"
                        >
                          View payment proof
                        </a>
                      </div>
                    ) : null}
                    {pgType === 'OFFLINE' && payment.note ? (
                      <div className="text-left text-sm text-gray-700 md:col-span-2">
                        <span className="font-semibold">Note:</span> {payment.note}
                      </div>
                    ) : null}
                    {pgType === 'OFFLINE' && paymentWallet ? (
                      <div className="text-left text-sm text-gray-700 md:col-span-2">
                        <span className="font-semibold">Collector wallet:</span>{' '}
                        {formatCentsAsCurrency(
                          moneyCents(paymentWallet.amount_cents ?? paymentWallet.amount),
                          currency
                        )}
                        {paymentWallet.last_balance_cents != null || paymentWallet.last_balance != null
                          ? ` · balance ${formatCentsAsCurrency(
                              moneyCents(
                                paymentWallet.last_balance_cents ?? paymentWallet.last_balance
                              ),
                              currency
                            )}`
                          : ''}
                      </div>
                    ) : null}
                    {ledgerRow.net_after_refunds_cents != null ? (
                      <div className="text-left text-sm text-gray-700">
                        <span className="font-semibold">Net after refunds:</span>{' '}
                        {formatCentsAsCurrency(moneyCents(ledgerRow.net_after_refunds_cents), currency)}
                        {ledgerRow.refundable_remaining_cents != null ? (
                          <span className="text-gray-500">
                            {' '}
                            · refundable{' '}
                            {formatCentsAsCurrency(moneyCents(ledgerRow.refundable_remaining_cents), currency)}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {card?.brand || card?.last4 ? (
                      <div className="text-left text-sm text-gray-600">
                        Card: {[card.brand, card.last4 ? `•••• ${card.last4}` : null].filter(Boolean).join(' ')}
                      </div>
                    ) : null}
                    <div className="text-left text-sm text-gray-600">
                      Created: {formatEasternDateTime(payment.created_at)}
                    </div>
                    <div className="text-left text-sm text-gray-600">
                      Updated: {formatEasternDateTime(payment.updated_at)}
                    </div>
                    {pgType !== 'OFFLINE' ? (
                      <div className="md:col-span-2">
                        <PaymentRefundControls
                          payment={payment}
                          currency={currency}
                          disabled={disabled}
                          onRefunded={refreshLedger}
                        />
                      </div>
                    ) : null}
                  </div>
                  {refunds.length > 0 ? (
                    <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Refunds</p>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead>
                            <tr className="text-xs text-gray-500">
                              <th className="pb-2 pr-4 font-medium">Refund ID</th>
                              <th className="pb-2 pr-4 font-medium">Amount</th>
                              <th className="pb-2 pr-4 font-medium">Status</th>
                              <th className="pb-2 pr-4 font-medium">Reason</th>
                              <th className="pb-2 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {refunds.map((ref) => (
                              <tr key={ref.id ?? ref.stripe_refund_id}>
                                <td className="py-2 pr-4 font-mono text-xs">{ref.stripe_refund_id || ref.id}</td>
                                <td className="py-2 pr-4 tabular-nums">
                                  {formatCentsAsCurrency(moneyCents(ref.amount_cents), currency)}
                                </td>
                                <td className="py-2 pr-4">
                                  <span className={`rounded px-2 py-0.5 text-xs ${refundStatusClass(ref.status)}`}>
                                    {ref.status}
                                  </span>
                                </td>
                                <td className="py-2 pr-4 text-gray-700">{ref.reason || '—'}</td>
                                <td className="py-2 text-gray-600">{formatEasternDateTime(ref.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {walletRows.length > 0 ? (
        <div className="rounded-lg bg-green-50 p-4">
          <h3 className="mb-4 text-left text-lg font-semibold text-green-800">Wallet transactions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Remark
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {walletRows.map((tx) => (
                  <tr key={tx.transaction_id}>
                    <td className="whitespace-nowrap px-4 py-3 text-left text-sm">
                      {formatEasternDateTime(tx.created_at)}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-3 text-left text-sm tabular-nums ${
                        Number(tx.amount_cents ?? tx.amount) < 0 ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {formatCentsAsCurrency(moneyCents(tx.amount_cents ?? tx.amount), currency)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-left text-sm tabular-nums">
                      {formatCentsAsCurrency(
                        moneyCents(tx.last_balance_cents ?? tx.last_balance),
                        currency
                      )}
                    </td>
                    <td className="px-4 py-3 text-left text-sm">{tx.remark || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {ledger.meta?.display_money_hint ? (
        <p className="text-xs text-gray-500">{ledger.meta.display_money_hint}</p>
      ) : null}
    </div>
  );
}

export default React.memo(OrderPaymentsLedgerPanel);
